import { getRouteApi } from '@tanstack/react-router';
import { useState } from 'react';

import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarDataHora, formatarInteiro } from '@/lib/formato';
import { useSessao } from '@/modulos/sessao/consultas';

import { ErroDaApi } from './api';
import { NotaDeEscopo } from './Avisos';
import {
  ACOES_DA_TRILHA,
  OPCOES_POR_PAGINA,
  PADRAO_POR_PAGINA,
  validarBuscaDaTrilha,
  type AcaoDaTrilha,
} from './busca';
import { useTrilhaPaginada } from './consultas';
import { NOME_DA_ACAO, formatarDocumento, type LinhaDaTrilha } from './tipos';
import { motivoDaLinha, nomeDoCampo, NOME_DO_RECURSO, recursoLegivel, valorLegivel } from './trilha';

const rota = getRouteApi('/cadastros/trilha');

/**
 * Modulo 01, ferramenta 3 — "registra autor, momento e motivo das alteracoes
 * relevantes, sem apagar a trilha original".
 *
 * ## Esta tela nao tem botao de escrever, e isso e o assunto dela
 *
 * A trilha e escrita pelo TRIGGER do banco, em toda tabela de dominio, dentro
 * da mesma transacao da mudanca. Nao ha rota que escreva nela, e a propria
 * aplicacao nao tem privilegio de `update` nem de `delete` sobre ela. O que se
 * faz aqui e LER — e o fato de nao haver nada para clicar e a garantia,
 * visivel, de que ninguem corrige o passado por esta porta. A tela diz isso na
 * palavra de quem usa (p.22): sem banco, transacao nem privilegio.
 *
 * ## O limite existe por causa do indice
 *
 * O filtro por recurso usa `trilha_por_recurso (empresa, recurso, recurso_id,
 * ocorrido_em desc)`. Sem filtro, a consulta varre a trilha inteira da empresa,
 * e por isso o servidor devolve uma pagina por vez, com o cursor da seguinte
 * (ocorrido_em, id). O rodape pagina o que chegou; a barra diz quando o que
 * chegou e so o mais recente, e "Carregar mais" traz os anteriores.
 */
export function PaginaTrilha() {
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();

  const recurso = busca.recurso ?? '';
  const recursoId = busca.recursoId ?? '';
  const acao = busca.acao;
  const pagina = busca.pagina ?? 1;
  const porPagina = busca.porPagina ?? PADRAO_POR_PAGINA;

  /* A TRILHA EM PAGINAS (06/10/2026): o servidor devolve 100 linhas e o
     cursor da seguinte; "Carregar mais" traz as anteriores. Antes o teto era
     de 500 sem cursor, e o que passava dele so se via filtrando. */
  const leTrilha = (useSessao().data?.permissoes ?? []).includes('auditoria.ler');
  const paginada = useTrilhaPaginada(
    {
      ...(recurso ? { recurso } : {}),
      ...(recursoId ? { recursoId } : {}),
      ...(acao ? { acao } : {}),
    },
    leTrilha,
  );
  const consulta = paginada.consulta;

  const [aberta, setAberta] = useState<string | null>(null);
  const linhas = paginada.itens;
  /* O TOTAL, e nao o tamanho da resposta. Os dois sao diferentes sempre que a
     empresa passa do teto do servidor, e e ai que "N registros" vira mentira. */
  const total = paginada.carregada ? paginada.total : linhas.length;
  const cortada = total > linhas.length;
  const registro = linhas.find((linha) => linha.id === aberta) ?? null;

  const paginas = Math.max(Math.ceil(linhas.length / porPagina), 1);
  const paginaSegura = Math.min(pagina, paginas);
  const inicio = (paginaSegura - 1) * porPagina;
  const visiveis = linhas.slice(inicio, inicio + porPagina);

  function irPara(estado: {
    readonly recurso?: string;
    readonly recursoId?: string;
    readonly acao?: AcaoDaTrilha | undefined;
    readonly pagina?: number;
    readonly porPagina?: number;
    readonly substituir?: boolean;
  }) {
    void navegar({
      search: validarBuscaDaTrilha({
        recurso: estado.recurso ?? recurso,
        recursoId: estado.recursoId ?? recursoId,
        acao: 'acao' in estado ? estado.acao : acao,
        pagina: estado.pagina ?? 1,
        porPagina: estado.porPagina ?? porPagina,
      }),
      ...(estado.substituir ? { replace: true } : {}),
    });
  }

  function limparFiltros() {
    irPara({ recurso: '', recursoId: '', acao: undefined });
  }

  const chips: readonly ChipFiltro[] = [
    ...(recurso
      ? [
          {
            id: 'recurso',
            rotulo: `Assunto: ${recursoLegivel(recurso)}`,
            aoRemover: () => {
              irPara({ recurso: '' });
            },
          },
        ]
      : []),
    ...(recursoId
      ? [
          {
            id: 'recursoId',
            rotulo: `Registro: ${recursoId}`,
            aoRemover: () => {
              irPara({ recursoId: '' });
            },
          },
        ]
      : []),
    ...(acao
      ? [
          {
            id: 'acao',
            rotulo: `Ação: ${NOME_DA_ACAO[acao] ?? acao}`,
            aoRemover: () => {
              irPara({ acao: undefined });
            },
          },
        ]
      : []),
  ];

  const colunas: readonly Coluna<LinhaDaTrilha>[] = [
    {
      chave: 'quando',
      rotulo: 'Quando',
      /* Data com hora nao quebra: "28/09/2026" em cima de "· 14:32" deixa de
         ser comparavel na vertical (p.18). */
      quebra: 'nunca',
      celula: (linha) => (
        <span className="numero text-tinta">{formatarDataHora(new Date(linha.ocorrido_em))}</span>
      ),
    },
    {
      chave: 'autor',
      rotulo: 'Autor',
      celula: (linha) =>
        linha.autor ? <TextoDaCelula texto={linha.autor} /> : <span className="text-tinta-secundaria">Fora do cadastro</span>,
    },
    {
      chave: 'acao',
      rotulo: 'O que houve',
      celula: (linha) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo whitespace-nowrap text-tinta">{NOME_DA_ACAO[linha.acao] ?? linha.acao}</span>
          <TextoDaCelula texto={recursoLegivel(linha.recurso)} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'motivo',
      rotulo: 'Motivo',
      celula: (linha) => {
        const motivo = motivoDaLinha(linha);
        return motivo ? <TextoDaCelula texto={motivo} /> : <span className="text-tinta-secundaria">Sem motivo declarado</span>;
      },
    },
    {
      chave: 'resultado',
      rotulo: 'Resultado',
      alinhamento: 'status',
      celula: (linha) => (linha.resultado === 'negado' ? 'Recusado' : 'Concluído'),
    },
  ];

  return (
    <>
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Cadastros e governança"
            titulo="Auditoria e histórico"
            subtitulo="Quem mudou, quando, por quê, e como estava antes"
          />

          <NotaDeEscopo
            titulo="A trilha não se corrige"
            detalhe="Esta lista não se edita: cada mudança do sistema entra aqui sozinha, com autor, data e motivo, e ninguém altera nem apaga o que já foi registrado. Por isso não há nada para editar nesta tela."
          />

          <BarraFiltros
            chips={chips}
            /* "0 registros" antes da trilha chegar seria um numero falso (p.32). */
            contagem={
              consulta.isPending
                ? null
                : cortada
                  ? `Os ${formatarInteiro(linhas.length)} mais recentes, de ${formatarInteiro(total)} registros — Carregar mais, abaixo da tabela, traz os anteriores`
                  : `${formatarInteiro(total)} ${total === 1 ? 'registro' : 'registros'}`
            }
            aoLimpar={limparFiltros}
          >
            <CampoSelecao
              rotulo="Assunto"
              valor={recurso}
              rotuloVazio="Todos"
              aplicado={Boolean(recurso)}
              opcoes={OPCOES_DE_ASSUNTO}
              aoMudar={(novo) => {
                irPara({ recurso: novo });
              }}
            />

            <CampoSelecao
              rotulo="Ação"
              valor={acao ?? ''}
              rotuloVazio="Todas"
              aplicado={Boolean(acao)}
              opcoes={ACOES_DA_TRILHA.map((chave) => ({ valor: chave, rotulo: NOME_DA_ACAO[chave] ?? chave }))}
              aoMudar={(novo) => {
                irPara({
                  acao: (ACOES_DA_TRILHA as readonly string[]).includes(novo) ? (novo as AcaoDaTrilha) : undefined,
                });
              }}
            />

            {/* Sem texto de ajuda embaixo: numa barra alinhada pela base, a
                ajuda subia rotulo e campo e desalinhava os tres controles. O
                placeholder e curto: a frase de antes ("Cole o identificador
                para ver a história de uma linha só") pedia 339 px e saia
                cortada a 390 e com o painel aberto. O "para que" esta na nota
                do painel do registro, onde o identificador aparece. */}
            <div className="min-w-0 flex-1">
              <CampoTexto
                rotulo="Registro"
                valor={recursoId}
                opcional
                placeholder="Identificador do registro"
                aplicado={Boolean(recursoId)}
                aoMudar={(novo) => {
                  irPara({ recursoId: novo, substituir: true });
                }}
              />
            </div>
          </BarraFiltros>

          <div data-painel-origem>
            <Tabela
              rotulo="Trilha de auditoria"
              colunas={colunas}
              linhas={visiveis}
              alturaDupla
              chaveDaLinha={(linha) => linha.id}
              carregando={consulta.isPending}
              selecionada={aberta}
              aoSelecionar={(linha) => {
                setAberta(linha.id);
              }}
              erro={
                consulta.isError ? (
                  <EstadoVazio
                    titulo={
                      consulta.error instanceof ErroDaApi && consulta.error.status === 401
                        ? 'Sua sessão não está aberta'
                        : 'Não foi possível carregar a trilha'
                    }
                    descricao={
                      consulta.error instanceof ErroDaApi
                        ? consulta.error.comoResolver
                        : 'A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário.'
                    }
                    acao={
                      <Botao
                        nivel="secundario"
                        onClick={() => {
                          void consulta.refetch();
                        }}
                      >
                        Carregar a trilha
                      </Botao>
                    }
                  />
                ) : null
              }
              vazio={
                chips.length > 0 ? (
                  <EstadoVazio
                    titulo="Nenhuma alteração com esses filtros"
                    descricao="Limpe os filtros, troque o assunto ou apague o identificador para ver a trilha inteira."
                    acao={
                      <Botao nivel="secundario" onClick={limparFiltros}>
                        Limpar filtros
                      </Botao>
                    }
                  />
                ) : (
                  <EstadoVazio
                    titulo="Nenhuma alteração registrada"
                    descricao="A trilha começa no primeiro cadastro gravado. Cada mudança entra aqui sozinha, com autor, data e motivo."
                  />
                )
              }
              rodape={
                <RodapeTabela
                  primeiro={inicio + 1}
                  ultimo={inicio + visiveis.length}
                  total={linhas.length}
                  pagina={paginaSegura}
                  paginas={paginas}
                  porPagina={porPagina}
                  opcoesPorPagina={OPCOES_POR_PAGINA}
                  aoMudarPagina={(nova) => {
                    irPara({ pagina: nova });
                  }}
                  aoMudarPorPagina={(novo) => {
                    irPara({ porPagina: novo, pagina: 1 });
                  }}
                />
              }
            />
            {paginada.temMais ? (
              <div className="mt-4 flex justify-center">
                <Botao nivel="secundario" carregando={paginada.carregandoMais} onClick={paginada.carregarMais}>
                  Carregar mais
                </Botao>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {registro ? (
        <DetalheDoRegistro
          key={registro.id}
          registro={registro}
          aoFechar={() => {
            setAberta(null);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * O filtro cobre o que a lista mostra (p.16): todo assunto que o banco audita,
 * e nao so os doze cadastros deste modulo. Em ordem alfabetica, que e como se
 * procura numa lista longa.
 */
const OPCOES_DE_ASSUNTO = Object.entries(NOME_DO_RECURSO)
  .filter(([chave]) => /^[a-z_]+\.[a-z_]+$/.test(chave))
  .map(([valor, rotulo]) => ({ valor, rotulo }))
  .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR'));

/**
 * O antes e o depois, campo a campo.
 *
 * A trilha guarda a LINHA INTEIRA em jsonb nos dois lados, e nao um diff: diff
 * calculado na escrita envelhece quando a tabela ganha coluna. O calculo do que
 * mudou e feito aqui, na leitura, e por isso ele sempre corresponde ao que esta
 * gravado. Nome de campo e valor saem legiveis (`trilha.ts`): data em dd/mm/aaaa
 * · 24 h (p.12), numero em pt-BR, e o nome da coluna na palavra de quem le.
 */
function DetalheDoRegistro({
  registro,
  aoFechar,
}: {
  readonly registro: LinhaDaTrilha;
  readonly aoFechar: () => void;
}) {
  const antes = registro.dados_antes ?? {};
  const depois = registro.dados_depois ?? {};
  /* O identificador da linha e o da empresa nao sao dado de quem le: o da
     linha ja esta em "Registro", e o da empresa ninguem escolhe. */
  const campos = [...new Set([...Object.keys(antes), ...Object.keys(depois)])]
    .filter((campo) => !CAMPOS_FORA_DA_LEITURA.has(campo))
    .sort((a, b) => nomeDoCampo(a).localeCompare(nomeDoCampo(b), 'pt-BR'));
  const mudados = campos.filter((campo) => JSON.stringify(antes[campo] ?? null) !== JSON.stringify(depois[campo] ?? null));
  /* Registro sem linha gravada (leitura, convite, senha): as duas abas ficam
     sem campo, e nenhuma pode mandar olhar a outra. */
  const semLinha =
    registro.acao === 'ler'
      ? 'A leitura não muda nenhum campo; o registro guarda quem viu e quando.'
      : 'Este registro guarda quem fez, quando e por quê, e não uma linha de cadastro.';

  return (
    <PainelDetalhe
      titulo={NOME_DA_ACAO[registro.acao] ?? registro.acao}
      subtitulo={recursoLegivel(registro.recurso)}
      dadosChave={
        <>
          <DadoChave rotulo="Quando">{formatarDataHora(new Date(registro.ocorrido_em))}</DadoChave>
          <DadoChave rotulo="Autor">{registro.autor ?? 'Fora do cadastro de pessoas'}</DadoChave>
          <DadoChave rotulo="Motivo">{motivoDaLinha(registro) ?? 'Sem motivo declarado'}</DadoChave>
          {/* O identificador tem 36 caracteres: ao lado do rotulo ele partia no
              meio. Embaixo do rotulo, na largura do painel, cabe numa linha —
              e e esta a forma de conferir e colar no filtro Registro. */}
          <div className="flex flex-col gap-1">
            <span className="txt-legenda font-normal text-tinta-secundaria">Registro</span>
            {registro.recurso_id ? (
              <>
                <span className="codigo whitespace-nowrap text-tinta">{registro.recurso_id}</span>
                <span className="txt-ajuda">Cole no filtro Registro para ver só a história desta linha.</span>
              </>
            ) : (
              <span className="txt-corpo text-tinta">—</span>
            )}
          </div>
        </>
      }
      abas={[
        {
          id: 'mudou',
          rotulo: 'O que mudou',
          conteudo:
            mudados.length === 0 ? (
              <EstadoVazio
                titulo="Nenhum campo mudou de valor"
                descricao={
                  campos.length === 0
                    ? semLinha
                    : 'O registro existe porque a operação aconteceu. Os valores gravados estão na aba ao lado.'
                }
              />
            ) : (
              <ul className="flex flex-col gap-3">
                {mudados.map((campo) => (
                  <li key={campo} className="flex flex-col gap-1 border-b border-borda-sutil pb-2">
                    <span className="txt-legenda font-normal text-tinta-secundaria">{nomeDoCampo(campo)}</span>
                    <span className="txt-apoio text-tinta-apagada">
                      {'De: '}
                      <Valor campo={campo} valor={antes[campo]} vazio="vazio" />
                    </span>
                    <span className="txt-corpo text-tinta">
                      {'Para: '}
                      <Valor campo={campo} valor={depois[campo]} vazio="vazio" trocou={antes[campo] !== undefined && antes[campo] !== null} />
                    </span>
                  </li>
                ))}
              </ul>
            ),
        },
        {
          id: 'linha',
          rotulo: 'A linha inteira',
          conteudo:
            campos.length === 0 ? (
              <EstadoVazio titulo="Nenhum campo gravado" descricao={semLinha} />
            ) : (
              <ul className="flex flex-col gap-2">
                {campos.map((campo) => (
                  <li key={campo} className="flex items-baseline justify-between gap-3">
                    <span className="txt-apoio shrink-0 text-tinta-secundaria">{nomeDoCampo(campo)}</span>
                    <span className="txt-apoio min-w-0 text-right text-pretty text-tinta">
                      <Valor campo={campo} valor={campo in depois ? depois[campo] : antes[campo]} vazio="—" />
                    </span>
                  </li>
                ))}
              </ul>
            ),
        },
      ]}
      aoFechar={aoFechar}
    />
  );
}

/** Colunas que a trilha grava e a leitura nao mostra (ver `DetalheDoRegistro`). */
const CAMPOS_FORA_DA_LEITURA: ReadonlySet<string> = new Set(['id', 'empresa_id']);

/**
 * Um valor da linha gravada, legivel (p.12 e p.22–23): CPF e CNPJ com a
 * mascara, e a REFERENCIA a outro cadastro (`cliente_id`, `filial_id`…) como
 * "Registro vinculado", com o identificador no `title` — a linha gravada nao
 * traz o nome da coisa, e o uuid cru, partido no meio, nao dizia nada a quem
 * le. `trocou` marca o lado "Para" de uma referencia que mudou de alvo.
 */
function Valor({
  campo,
  valor,
  vazio,
  trocou = false,
}: {
  readonly campo: string;
  readonly valor: unknown;
  readonly vazio: string;
  readonly trocou?: boolean;
}) {
  const legivel = valorLegivel(valor);
  if (legivel.texto === '') return <>{vazio}</>;
  if (campo === 'documento' && /^\d{11}$|^\d{14}$/.test(legivel.texto)) return <>{formatarDocumento(legivel.texto)}</>;
  if (legivel.identificador && campo.endsWith('_id')) {
    return <span title={legivel.texto}>{trocou ? 'Outro registro vinculado' : 'Registro vinculado'}</span>;
  }
  return legivel.identificador ? <span className="codigo break-all whitespace-normal!">{legivel.texto}</span> : <>{legivel.texto}</>;
}
